<template>
    <div class="wa-chat-header" :class="{ 'wa-chat-header--mobile': mobileMode }">
        <!-- Geri butonu (sadece mobile mode) -->
        <button
            v-if="mobileMode"
            class="wa-chat-header__back-btn"
            @click="$emit('back')"
        >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>

        <!-- Avatar + Contact Info -->
        <div class="wa-chat-header__contact" @click="$emit('view-contact')">
            <div class="wa-chat-header__avatar" :style="avatarStyle">
                <img
                    v-if="avatarUrl && !avatarError"
                    :src="avatarUrl"
                    class="wa-chat-header__avatar-img"
                    @error="avatarError = true"
                    alt=""
                />
                <span v-else class="wa-chat-header__avatar-initials">{{ initials }}</span>
            </div>
            <div class="wa-chat-header__info">
                <div class="wa-chat-header__name">{{ displayName }}</div>
                <div class="wa-chat-header__subtitle">
                    <!-- isim ile telefon aynıysa subtitle'da telefonu tekrar gösterme -->
                    <span v-if="phoneNumber && phoneNumber !== displayName" class="wa-chat-header__phone">{{ phoneNumber }}</span>
                    <span v-if="channelLabel" class="wa-chat-header__channel-badge" :class="channelBadgeClass">
                        {{ channelLabel }}
                    </span>
                    <span v-if="contact.lastConversation && contact.lastConversation.status === 'destroyed'" class="wa-chat-header__status wa-chat-header__status--closed">
                        {{ $t("whatsappUi.statusClosed") }}
                    </span>
                    <span v-else-if="contact.lastConversation" class="wa-chat-header__status wa-chat-header__status--open">
                        {{ $t("whatsappUi.statusOpen") }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Actions -->
        <div class="wa-chat-header__actions">
            <!-- WABA 24h warning -->
            <div
                v-if="showWaba24hWarning"
                class="wa-chat-header__waba-warning"
                v-tippy="{ content: $t('whatsappUi.waba24hWindowTooltip') }"
            >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#e67e22"/>
                </svg>
                <span>{{ $t("whatsappUi.waba24hExpiredBadge") }}</span>
            </div>

            <!-- Template message (WABA only) -->
            <button
                v-if="isWabaChannel"
                class="wa-chat-header__btn"
                v-tippy="{ content: $t('whatsappUi.templateMessageTooltip') }"
                @click="$emit('open-template')"
            >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
            </button>

            <!-- Search in chat -->
            <button
                class="wa-chat-header__btn"
                v-tippy="{ content: $t('whatsappUi.searchInChatTooltip') }"
                @click="$emit('search')"
            >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <path d="M11 3a8 8 0 100 16 8 8 0 000-16zM21 21l-4.35-4.35" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
            </button>

            <!-- Toggle user info -->
            <button
                class="wa-chat-header__btn"
                v-tippy="{ content: $t('whatsappUi.contactInfoTooltip') }"
                @click="$emit('view-contact')"
            >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <circle cx="12" cy="8" r="3" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M5 20c0-4 3.134-7 7-7s7 3 7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
            </button>

            <!-- More options -->
            <a-dropdown :trigger="['click']" placement="bottomRight">
                <button class="wa-chat-header__btn" v-tippy="{ content: $t('whatsappUi.moreMenuTooltip') }">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                        <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
                        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                        <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
                    </svg>
                </button>
                <a-menu slot="overlay">
                    <a-menu-item @click="$emit('view-contact')">{{ $t("whatsappUi.menuContactInfo") }}</a-menu-item>
                    <a-menu-item v-if="contact.lastConversation && contact.lastConversation.status !== 'destroyed'" @click="$emit('close-conversation')">{{ $t("whatsappUi.menuCloseConversation") }}</a-menu-item>
                </a-menu>
            </a-dropdown>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { isMaskContactInfo, maskContactInfo } from "../../../../utils/utils";

const AVATAR_COLORS = [
    "#128C7E", "#25D366", "#075E54", "#34B7F1",
    "#00BCD4", "#4CAF50", "#8BC34A", "#FF9800",
];

export default {
    name: "WAChatHeader",
    props: {
        contact: {
            type: Object,
            required: true,
        },
        channel: {
            type: Object,
            default: null,
        },
        mobileMode: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        avatarError: false,
    }),
    computed: {
        ...mapState({
            whatsappContactStatus: (state) => state.contact.whatsappContactStatus,
        }),
        displayName() {
            const contact = this.contact;
            if (isMaskContactInfo()) {
                return maskContactInfo(contact.fullname || contact.name || contact.whatsappNumber || "Bilinmeyen");
            }
            return contact.fullname || contact.name || contact.whatsappNumber || contact.email || "Bilinmeyen";
        },
        initials() {
            const name = this.displayName || "?";
            return name
                .split(" ")
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
        },
        avatarUrl() {
            if (this.avatarError) return null;
            const img = this.$_.get(this.contact, "whatsappInfo.profilePicThumbObj.img");
            if (img) return img;
            return this.$_.get(this.contact, "profilePicture") || null;
        },
        avatarStyle() {
            if (this.avatarUrl && !this.avatarError) return {};
            const name = this.displayName || "?";
            const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
            return { backgroundColor: AVATAR_COLORS[idx] };
        },
        phoneNumber() {
            if (isMaskContactInfo()) return null;
            return this.contact.whatsappNumber
                || this.$_.get(this.contact, "channelRefId")
                || null;
        },
        isWabaChannel() {
            const type = this.channel && this.channel.type;
            return type === "whatsapp-business-api" || type === "whatsapp-business-api-supsis";
        },
        channelLabel() {
            if (!this.channel) return null;
            const labels = {
                "whatsapp-business-api": "WABA",
                "whatsapp-business-api-supsis": "WABA",
                "whatsapp-unipile": "Unipile",
                "whatsapp-v2": "QR",
                "whatsapp-qr": "QR",
            };
            return labels[this.channel.type] || null;
        },
        channelBadgeClass() {
            const type = this.channel && this.channel.type;
            if (type === "whatsapp-business-api" || type === "whatsapp-business-api-supsis") {
                return "wa-chat-header__channel-badge--waba";
            }
            if (type === "whatsapp-unipile") {
                return "wa-chat-header__channel-badge--unipile";
            }
            return "";
        },
        showWaba24hWarning() {
            if (!this.isWabaChannel) return false;
            const whatsappNumber = this.contact.whatsappNumber || this.$_.get(this.contact, "channelRefId");
            const channelId = this.channel && this.channel._id;
            if (!whatsappNumber || !channelId) return false;
            const statusKey = channelId + "_" + whatsappNumber;
            const status = this.whatsappContactStatus[statusKey];
            if (!status) return false;
            return status !== "active";
        },
    },
};
</script>

<style lang="scss" scoped>
.wa-chat-header {
    height: 60px;
    background: #f0f2f5;
    display: flex;
    align-items: center;
    padding: 0 16px;
    gap: 12px;
    border-bottom: 1px solid #e9edef;
    flex-shrink: 0;

    &--mobile {
        padding: 0 4px;
        gap: 2px;

        // Avatar küçül
        .wa-chat-header__avatar {
            width: 34px;
            height: 34px;
        }
        .wa-chat-header__avatar-initials {
            font-size: 12px;
        }

        // Kişi alanı gap azalt
        .wa-chat-header__contact {
            gap: 8px;
        }

        // İsim ve subtitle küçül
        .wa-chat-header__name {
            font-size: 13px;
        }
        .wa-chat-header__subtitle {
            flex-wrap: nowrap;
            overflow: hidden;
            gap: 4px;
        }
        .wa-chat-header__phone {
            font-size: 11px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 120px;
        }
        .wa-chat-header__channel-badge {
            font-size: 9px;
            padding: 1px 4px;
            flex-shrink: 0;
        }
        // Durum badge'ini gizle (yer açmak için)
        .wa-chat-header__status {
            display: none;
        }

        // WABA uyarı: sadece ikon göster, metin gizle
        .wa-chat-header__waba-warning {
            padding: 3px 4px;
            margin-right: 0;
            span {
                display: none;
            }
        }

        // Aksiyon butonları küçült
        .wa-chat-header__actions {
            gap: 0;
        }
        .wa-chat-header__btn {
            width: 30px;
            height: 30px;

            svg {
                width: 17px;
                height: 17px;
            }
        }
    }

    &__back-btn {
        width: 36px;
        height: 36px;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #128C7E;
        border-radius: 50%;
        flex-shrink: 0;
        outline: none;
        padding: 0;

        &:hover {
            background: rgba(18, 140, 126, 0.1);
        }
    }

    &__contact {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        cursor: pointer;
        min-width: 0;

        &:hover .wa-chat-header__name {
            color: #128C7E;
        }
    }

    &__avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: #dfe5e7;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
    }

    &__avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &__avatar-initials {
        color: #fff;
        font-size: 15px;
        font-weight: 600;
    }

    &__info {
        min-width: 0;
        flex: 1;
    }

    &__name {
        font-size: 16px;
        font-weight: 500;
        color: #111b21;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: color 0.15s;
    }

    &__subtitle {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    &__phone {
        font-size: 13px;
        color: #667781;
    }

    &__channel-badge {
        font-size: 10px;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 4px;
        background: #e9edef;
        color: #54656f;
        text-transform: uppercase;
        letter-spacing: 0.3px;

        &--waba {
            background: #d9fdd3;
            color: #128C7E;
        }

        &--unipile {
            background: #dceffe;
            color: #0069c2;
        }
    }

    &__status {
        font-size: 11px;
        font-weight: 500;
        padding: 1px 6px;
        border-radius: 10px;

        &--open {
            background: #d9fdd3;
            color: #128C7E;
        }

        &--closed {
            background: #f0f2f5;
            color: #667781;
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
    }

    &__waba-warning {
        display: flex;
        align-items: center;
        gap: 5px;
        background: #fef9e7;
        border: 1px solid #f9a825;
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 11px;
        color: #e67e22;
        font-weight: 500;
        cursor: default;
        margin-right: 4px;
    }

    &__btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #54656f;
        transition: background-color 0.15s, color 0.15s;
        outline: none;

        &:hover {
            background: rgba(0,0,0,0.06);
            color: #111b21;
        }

        &--active {
            background: rgba(18, 140, 126, 0.1);
            color: #128C7E;
        }
    }
}
</style>
